import React, { useEffect, useRef, useCallback, useState } from "react";
import { CPU, avrInstruction, AVRTimer, timer0Config, timer1Config, timer2Config, AVRIOPort, portBConfig, portCConfig, portDConfig, PinState, AVRUSART, usart0Config } from "avr8js";
import "./ArduinoSimulator.css";
import { WOKWI_PINS } from "./wokwi-pins";
import { DHTSimulator } from "./sensors/dht-simulator";
import { HCSR04Simulator } from "./sensors/hc-sr04-simulator";

const LED_COLORS = ["green", "red", "blue", "yellow", "orange", "white"];

function parseIntelHex(hexString) {
    const lines = hexString.split('\n').filter(l => l.startsWith(':'));
    let maxAddr = 0;
    for (const line of lines) {
        const byteCount = parseInt(line.substring(1, 3), 16);
        const address = parseInt(line.substring(3, 7), 16);
        const recType = parseInt(line.substring(7, 9), 16);
        if (recType === 0x00) {
            const end = address + byteCount;
            if (end > maxAddr) maxAddr = end;
        }
    }
    const data = new Uint8Array(maxAddr);
    for (const line of lines) {
        const byteCount = parseInt(line.substring(1, 3), 16);
        const address = parseInt(line.substring(3, 7), 16);
        const recType = parseInt(line.substring(7, 9), 16);
        if (recType === 0x00) {
            for (let i = 0; i < byteCount; i++) {
                data[address + i] = parseInt(line.substring(9 + i * 2, 11 + i * 2), 16);
            }
        }
    }
    return data;
}

function pinToPort(pin) {
    if (pin == null) return null;
    if (typeof pin === 'string' && pin.startsWith('A')) return { port: 'C', bit: parseInt(pin.substring(1), 10) };
    const p = parseInt(pin, 10);
    if (isNaN(p)) return null;
    if (p >= 0 && p <= 7) return { port: 'D', bit: p };
    if (p >= 8 && p <= 13) return { port: 'B', bit: p - 8 };
    return null;
}

const WIRE_COLORS = ['#ff4757', '#00e676', '#26c6da', '#feca57', '#a55eea', '#ffffff', '#8b8fa3'];

function generateOrthogonalPath(x1, y1, x2, y2, index = 0) {
    // Add a deterministic offset based on the wire index to spread out horizontal segments
    const offset = (index % 8) * 8 - 28;
    const midY = ((y1 + y2) / 2) + offset;
    return `M ${x1} ${y1} V ${midY} H ${x2} V ${y2}`;
}

/**
 * ArduinoSimulator – Visual Drag-and-Drop Circuit Builder
 */
const ArduinoSimulator = ({ hexBase64, onClose }) => {
    const animFrameRef = useRef(null);
    const [running, setRunning] = useState(false);

    // AVR peripheral refs
    const portBRef = useRef(null);
    const portCRef = useRef(null);
    const portDRef = useRef(null);
    const usartRef = useRef(null);

    // Serial monitor
    const [serialOutput, setSerialOutput] = useState("");
    const serialBufferRef = useRef("");
    const serialPanelRef = useRef(null);
    const [serialInput, setSerialInput] = useState("");
    const serialInputQueue = useRef([]);

    // --- Visual Circuit Builder State ---
    const [components, setComponents] = useState([
        { id: 'uno', type: 'uno', x: 300, y: 150 },
    ]);
    const [wires, setWires] = useState([]);

    // Interaction state
    const [draggingComp, setDraggingComp] = useState(null); // { id, initialCompX, initialCompY, initialMouseX, initialMouseY }
    const [drawingWire, setDrawingWire] = useState(null); // { compId, pin, startX, startY, endX, endY }
    const [zoom, setZoom] = useState(1);

    // Refs for simulation loop
    const componentsRef = useRef(components);
    const wiresRef = useRef(wires);
    const ledDOMRefs = useRef({});
    const buttonStatesRef = useRef({});

    // DHT state
    const dhtSimulatorsRef = useRef({});
    const [dhtValues, setDhtValues] = useState({});
    const [activeSensorId, setActiveSensorId] = useState(null);
    const dhtValuesRef = useRef(dhtValues);

    // HC-SR04 state
    const hcsr04SimulatorsRef = useRef({});
    const [hcsr04Values, setHcsr04Values] = useState({});
    const hcsr04ValuesRef = useRef(hcsr04Values);

    useEffect(() => { componentsRef.current = components; }, [components]);
    useEffect(() => { wiresRef.current = wires; }, [wires]);
    useEffect(() => { dhtValuesRef.current = dhtValues; }, [dhtValues]);
    useEffect(() => { hcsr04ValuesRef.current = hcsr04Values; }, [hcsr04Values]);

    // Build the wiring map for the simulation loop
    const simMapRef = useRef({ leds: [], buttons: [], dhtSensors: [], hcsr04Sensors: [] });
    useEffect(() => {
        const map = { leds: [], buttons: [], dhtSensors: [], hcsr04Sensors: [] };
        wires.forEach(w => {
            let unoEnd = null;
            let compEnd = null;
            if (w.from.compId === 'uno') { unoEnd = w.from; compEnd = w.to; }
            else if (w.to.compId === 'uno') { unoEnd = w.to; compEnd = w.from; }

            if (unoEnd && compEnd) {
                const comp = components.find(c => c.id === compEnd.compId);
                if (comp && comp.type === 'led') {
                    if (compEnd.pin === 'A') map.leds.push({ compId: comp.id, unoPin: unoEnd.pin });
                } else if (comp && comp.type === 'btn') {
                    map.buttons.push({ compId: comp.id, unoPin: unoEnd.pin });
                } else if (comp && (comp.type === 'dht11' || comp.type === 'dht22')) {
                    if (compEnd.pin === 'DATA') map.dhtSensors.push({ compId: comp.id, unoPin: unoEnd.pin });
                } else if (comp && comp.type === 'hc-sr04') {
                    const entry = map.hcsr04Sensors.find(s => s.compId === comp.id) || { compId: comp.id, trigPin: null, echoPin: null };
                    if (compEnd.pin === 'TRIG') entry.trigPin = unoEnd.pin;
                    if (compEnd.pin === 'ECHO') entry.echoPin = unoEnd.pin;
                    if (!map.hcsr04Sensors.includes(entry)) map.hcsr04Sensors.push(entry);
                }
            }
        });
        simMapRef.current = map;
    }, [wires, components]);

    /** Boot (or reboot) the virtual CPU */
    const boot = useCallback(() => {
        if (animFrameRef.current) {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = null;
        }

        try {
            const rawBinary = atob(hexBase64);
            const hexData = parseIntelHex(rawBinary);

            const progMem = new Uint16Array(0x4000);
            const progBytes = new Uint8Array(progMem.buffer);
            for (let i = 0; i < hexData.length; i++) progBytes[i] = hexData[i];

            const cpu = new CPU(progMem);
            /* eslint-disable no-unused-vars */
            const timer0 = new AVRTimer(cpu, timer0Config);
            const timer1 = new AVRTimer(cpu, timer1Config);
            const timer2 = new AVRTimer(cpu, timer2Config);
            /* eslint-enable no-unused-vars */
            const portB = new AVRIOPort(cpu, portBConfig);
            const portC = new AVRIOPort(cpu, portCConfig);
            const portD = new AVRIOPort(cpu, portDConfig);

            portBRef.current = portB;
            portCRef.current = portC;
            portDRef.current = portD;

            Object.values(dhtSimulatorsRef.current).forEach(sim => sim.reset());
            Object.values(hcsr04SimulatorsRef.current).forEach(sim => sim.reset());

            const usart = new AVRUSART(cpu, usart0Config, 16e6);
            usartRef.current = usart;

            serialBufferRef.current = "";
            setSerialOutput("");
            usart.onByteTransmit = (byte) => {
                serialBufferRef.current += String.fromCharCode(byte);
            };

            setRunning(true);

            const CYCLES_PER_FRAME = 500000;
            const SAMPLE_INTERVAL = 64;

            // Duty cycle tracking
            const highCount = {};
            const totalCount = {};

            const runSimulation = () => {

                // Initialize counters for any new LEDs
                componentsRef.current.forEach(c => {
                    if (c.type === 'led') {
                        if (highCount[c.id] === undefined) {
                            highCount[c.id] = 0;
                            totalCount[c.id] = 0;
                        }
                    }
                });

                const map = simMapRef.current;

                // Pre-calculate active DHT sensors for this frame to keep the tight loop fast
                const activeDhts = map.dhtSensors.map(sensorMap => {
                    const dhtSim = dhtSimulatorsRef.current[sensorMap.compId];
                    const mapping = pinToPort(sensorMap.unoPin);
                    if (dhtSim && mapping) {
                        const values = dhtValuesRef.current[sensorMap.compId];
                        if (values) dhtSim.setValues(values.temp, values.humidity);
                        return {
                            sim: dhtSim,
                            port: mapping.port === 'B' ? portB : (mapping.port === 'C' ? portC : portD),
                            bit: mapping.bit
                        };
                    }
                    return null;
                }).filter(Boolean);

                const activeHcsr04s = map.hcsr04Sensors.map(sensorMap => {
                    const sim = hcsr04SimulatorsRef.current[sensorMap.compId];
                    const trigMapping = pinToPort(sensorMap.trigPin);
                    const echoMapping = pinToPort(sensorMap.echoPin);
                    if (sim && trigMapping && echoMapping) {
                        const values = hcsr04ValuesRef.current[sensorMap.compId];
                        if (values) sim.setDistance(values.distance);
                        return {
                            sim,
                            trigPort: trigMapping.port === 'B' ? portB : (trigMapping.port === 'C' ? portC : portD),
                            trigBit: trigMapping.bit,
                            echoPort: echoMapping.port === 'B' ? portB : (echoMapping.port === 'C' ? portC : portD),
                            echoBit: echoMapping.bit
                        };
                    }
                    return null;
                }).filter(Boolean);

                for (let i = 0; i < CYCLES_PER_FRAME; i++) {
                    avrInstruction(cpu);
                    cpu.tick();

                    // HC-SR04 Sensor simulation
                    for (const hc of activeHcsr04s) {
                        const { sim, trigPort, trigBit, echoPort, echoBit } = hc;
                        const trigPortVal = cpu.data[trigPort.portConfig.PORT];
                        const trigHigh = !!(trigPortVal & (1 << trigBit));

                        const echoOutput = sim.tick(cpu.cycles, trigHigh);
                        echoPort.setPin(echoBit, echoOutput);
                    }

                    // DHT Sensor simulation (Cycle-accurate)
                    for (const dht of activeDhts) {
                        const { sim, port, bit } = dht;
                        const ddr = cpu.data[port.portConfig.DDR];
                        const portVal = cpu.data[port.portConfig.PORT];
                        const isOutput = !!(ddr & (1 << bit));
                        const isHigh = !!(portVal & (1 << bit));

                        const output = sim.tick(cpu.cycles, isOutput, isHigh);
                        // If output is null, it means sensor is High-Z (pulled HIGH by resistor)
                        // If false, sensor is pulling the line LOW.
                        if (output !== null) {
                            port.setPin(bit, output);
                        } else {
                            port.setPin(bit, true); // High-Z / Pull-up
                        }
                    }

                    if (serialInputQueue.current.length > 0 && !usart.rxBusy) {
                        usart.writeByte(serialInputQueue.current.shift());
                    }

                    if (i % SAMPLE_INTERVAL === 0) {
                        const currentWires = wiresRef.current;
                        const currentComps = componentsRef.current;
                        const btnStates = buttonStatesRef.current;

                        // 1. Build Adjacency List for the graph
                        const adj = {};
                        const addEdge = (u, v) => {
                            if (!adj[u]) adj[u] = [];
                            if (!adj[v]) adj[v] = [];
                            adj[u].push(v);
                            adj[v].push(u);
                        };

                        currentWires.forEach(w => {
                            addEdge(`${w.from.compId}:${w.from.pin}`, `${w.to.compId}:${w.to.pin}`);
                        });

                        currentComps.forEach(c => {
                            if (c.type === 'btn') {
                                // Buttons connect their own left/right pins internally
                                addEdge(`${c.id}:1.l`, `${c.id}:1.r`);
                                addEdge(`${c.id}:2.l`, `${c.id}:2.r`);
                                // When pressed, they connect pin 1 to pin 2
                                if (btnStates[c.id]) {
                                    addEdge(`${c.id}:1.l`, `${c.id}:2.l`);
                                    addEdge(`${c.id}:1.r`, `${c.id}:2.r`);
                                }
                            }
                        });

                        const hasPathTo = (startNode, targetPredicate) => {
                            if (!adj[startNode]) return false;
                            const visited = new Set([startNode]);
                            const queue = [startNode];

                            while (queue.length > 0) {
                                const curr = queue.shift();
                                if (targetPredicate(curr)) return true;
                                const neighbors = adj[curr] || [];
                                for (const n of neighbors) {
                                    if (!visited.has(n)) {
                                        visited.add(n);
                                        queue.push(n);
                                    }
                                }
                            }
                            return false;
                        };

                        const isSource = (node) => {
                            if (node === 'uno:5V' || node === 'uno:3.3V' || node === 'uno:VIN') return true;
                            if (node.startsWith('uno:')) {
                                const pinName = node.split(':')[1];
                                const mapping = pinToPort(pinName);
                                if (mapping) {
                                    const port = mapping.port === 'B' ? portB : (mapping.port === 'C' ? portC : portD);
                                    return port.pinState(mapping.bit) === PinState.High;
                                }
                            }
                            return false;
                        };

                        const isSink = (node) => {
                            if (node.startsWith('uno:GND')) return true;
                            if (node.startsWith('uno:')) {
                                const pinName = node.split(':')[1];
                                const mapping = pinToPort(pinName);
                                if (mapping) {
                                    const port = mapping.port === 'B' ? portB : (mapping.port === 'C' ? portC : portD);
                                    return port.pinState(mapping.bit) === PinState.Low;
                                }
                            }
                            return false;
                        };

                        currentComps.forEach(c => {
                            if (c.type === 'led') {
                                const anodeNode = `${c.id}:A`;
                                const cathodeNode = `${c.id}:C`;

                                const isHigh = hasPathTo(anodeNode, isSource) && hasPathTo(cathodeNode, isSink);

                                totalCount[c.id]++;
                                if (isHigh) highCount[c.id]++;
                            }
                        });
                    }
                }

                // Apply brightness to DOM
                componentsRef.current.forEach(c => {
                    if (c.type !== 'led') return;
                    const compId = c.id;
                    const total = totalCount[compId];
                    if (!total) return;
                    let brightness = highCount[compId] / total;
                    highCount[compId] = 0;
                    totalCount[compId] = 0;

                    const el = ledDOMRefs.current[compId];
                    if (el) {
                        el.value = brightness > 0.01;
                        el.brightness = brightness;
                    }
                });

                // Flush serial
                if (serialBufferRef.current.length > 0) {
                    const chunk = serialBufferRef.current;
                    serialBufferRef.current = "";
                    setSerialOutput(prev => {
                        const combined = prev + chunk;
                        return combined.length > 10000 ? combined.slice(combined.length - 10000) : combined;
                    });
                }

                animFrameRef.current = requestAnimationFrame(runSimulation);
            };

            animFrameRef.current = requestAnimationFrame(runSimulation);
        } catch (err) {
            console.error("Failed to boot:", err);
            setRunning(false);
        }
    }, [hexBase64]);

    useEffect(() => {
        if (hexBase64) boot();
        return () => {
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        };
    }, [hexBase64, boot]);

    useEffect(() => {
        if (serialPanelRef.current) {
            serialPanelRef.current.scrollTop = serialPanelRef.current.scrollHeight;
        }
    }, [serialOutput]);

    const handleStop = () => {
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        setRunning(false);
    };

    const handleRestart = () => boot();

    /* --- Button Interactions --- */
    /* --- Button Interactions --- */
    const handleButtonPress = (compId) => {
        buttonStatesRef.current[compId] = true;
        const entries = simMapRef.current.buttons.filter(b => b.compId === compId);

        const isGndWired = wiresRef.current.some(w =>
            (w.from.compId === compId && w.to.compId === 'uno' && w.to.pin.startsWith('GND')) ||
            (w.to.compId === compId && w.from.compId === 'uno' && w.from.pin.startsWith('GND'))
        );

        entries.forEach(entry => {
            const pinNum = parseInt(entry.unoPin, 10);
            if (String(pinNum) !== entry.unoPin) return; // Ignore if connected to power/GND

            const mapping = pinToPort(pinNum);
            if (!mapping) return;
            const port = mapping.port === 'D' ? portDRef.current : portBRef.current;
            if (port) port.setPin(mapping.bit, isGndWired ? PinState.Low : PinState.High);
        });
    };

    const handleButtonRelease = (compId) => {
        buttonStatesRef.current[compId] = false;
        const entries = simMapRef.current.buttons.filter(b => b.compId === compId);

        const isGndWired = wiresRef.current.some(w =>
            (w.from.compId === compId && w.to.compId === 'uno' && w.to.pin.startsWith('GND')) ||
            (w.to.compId === compId && w.from.compId === 'uno' && w.from.pin.startsWith('GND'))
        );

        entries.forEach(entry => {
            const pinNum = parseInt(entry.unoPin, 10);
            if (String(pinNum) !== entry.unoPin) return;

            const mapping = pinToPort(pinNum);
            if (!mapping) return;
            const port = mapping.port === 'D' ? portDRef.current : portBRef.current;
            if (port) port.setPin(mapping.bit, isGndWired ? PinState.High : PinState.Low);
        });
    };

    const handleButtonPressRef = useRef(handleButtonPress);
    const handleButtonReleaseRef = useRef(handleButtonRelease);
    handleButtonPressRef.current = handleButtonPress;
    handleButtonReleaseRef.current = handleButtonRelease;

    /* --- Canvas Logic --- */
    const getPinCoords = (compId, pinName, comps = components) => {
        const comp = comps.find(c => c.id === compId);
        if (!comp) return { x: 0, y: 0 };
        const pin = WOKWI_PINS[comp.type]?.find(p => p.name === pinName);
        if (!pin) return { x: comp.x, y: comp.y };

        return {
            x: comp.x + pin.x,
            y: comp.y + pin.y
        };
    };

    const handleCanvasMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const mouseX = (e.clientX - rect.left) / zoom;
        const mouseY = (e.clientY - rect.top) / zoom;

        if (draggingComp) {
            const dx = (e.clientX - draggingComp.initialMouseX) / zoom;
            const dy = (e.clientY - draggingComp.initialMouseY) / zoom;
            setComponents(prev => prev.map(c =>
                c.id === draggingComp.id ? { ...c, x: draggingComp.initialCompX + dx, y: draggingComp.initialCompY + dy } : c
            ));
        }

        if (drawingWire) {
            setDrawingWire(prev => ({ ...prev, endX: mouseX, endY: mouseY }));
        }
    };

    const handleCanvasMouseUp = () => {
        if (draggingComp) setDraggingComp(null);
        if (drawingWire) setDrawingWire(null); // Cancel if dropped in empty space
    };

    const handlePinMouseDown = (e, compId, pinName) => {
        e.stopPropagation();
        if (drawingWire) return;

        // Remove existing wire connected to this pin
        setWires(prev => prev.filter(w =>
            !(w.from.compId === compId && w.from.pin === pinName) &&
            !(w.to.compId === compId && w.to.pin === pinName)
        ));

        const coords = getPinCoords(compId, pinName);
        setDrawingWire({ compId, pin: pinName, startX: coords.x, startY: coords.y, endX: coords.x, endY: coords.y });
    };

    const handlePinMouseUp = (e, compId, pinName) => {
        e.stopPropagation();
        if (!drawingWire) return;
        if (drawingWire.compId === compId) { setDrawingWire(null); return; } // Same pin

        setWires(prev => [...prev, {
            id: `w-${Date.now()}`,
            from: { compId: drawingWire.compId, pin: drawingWire.pin },
            to: { compId, pin: pinName },
            color: WIRE_COLORS[prev.length % WIRE_COLORS.length]
        }]);
        setDrawingWire(null);
    };

    const handleWireClick = (e, wireId) => {
        e.stopPropagation();
        setWires(prev => prev.filter(w => w.id !== wireId));
    };

    /* --- Toolbar Logic --- */
    const addComponent = (type) => {
        const id = `${type}-${Date.now()}`;
        const newComp = { id, type, x: 50 + Math.random() * 50, y: 50 + Math.random() * 50 };
        if (type === 'led') newComp.color = LED_COLORS[components.length % LED_COLORS.length];
        if (type === 'btn') newComp.color = LED_COLORS[(components.length + 1) % LED_COLORS.length];
        
        if (type === 'dht11' || type === 'dht22') {
            dhtSimulatorsRef.current[id] = new DHTSimulator(type.toUpperCase());
            setDhtValues(prev => ({
                ...prev,
                [id]: { 
                    temp: 25, 
                    humidity: 60,
                    type: type.toUpperCase()
                }
            }));
        } else if (type === 'hc-sr04') {
            hcsr04SimulatorsRef.current[id] = new HCSR04Simulator();
            setHcsr04Values(prev => ({
                ...prev,
                [id]: { distance: 100 }
            }));
        }
        
        setComponents([...components, newComp]);
    };

    const removeComponent = (id) => {
        if (id === 'uno') return;
        setComponents(prev => prev.filter(c => c.id !== id));
        setWires(prev => prev.filter(w => w.from.compId !== id && w.to.compId !== id));
        
        if (dhtSimulatorsRef.current[id]) delete dhtSimulatorsRef.current[id];
        setDhtValues(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });

        if (hcsr04SimulatorsRef.current[id]) delete hcsr04SimulatorsRef.current[id];
        setHcsr04Values(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });

        if (activeSensorId === id) setActiveSensorId(null);
    };

    return (
        <div className="simulator-overlay" onClick={onClose}>
            <div className="simulator-card" onClick={(e) => e.stopPropagation()}>
                <div className="simulator-header">
                    <div className="simulator-header-left">
                        <span className="component-group-icon">🎛️</span>
                        <h3 className="simulator-title">Arduino Simulator</h3>
                    </div>
                    <button className="simulator-close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="simulator-status-bar">
                    <span className={`status-dot ${running ? "running" : "stopped"}`} />
                    <span className="status-label">{running ? "Running" : "Stopped"}</span>
                    <div className="sim-toolbar">
                        <button className="sim-add-btn sim-zoom-btn" onClick={() => setZoom(z => Math.max(0.5, z - 0.1))}>-</button>
                        <span className="sim-zoom-label">{Math.round(zoom * 100)}%</span>
                        <button className="sim-add-btn sim-zoom-btn" onClick={() => setZoom(z => Math.min(2.0, z + 0.1))}>+</button>
                        <div className="sim-toolbar-divider" />
                        <button className="sim-add-btn" onClick={() => addComponent('led')}>+ LED</button>
                        <button className="sim-add-btn" onClick={() => addComponent('btn')}>+ Button</button>
                        <select 
                            className="sim-add-btn" 
                            style={{ cursor: 'pointer', outline: 'none', WebkitAppearance: 'none', paddingRight: '12px', background: 'rgba(0, 172, 193, 0.1) url("data:image/svg+xml;utf8,<svg fill=\'%2326c6da\' height=\'24\' viewBox=\'0 0 24 24\' width=\'24\' xmlns=\'http://www.w3.org/2000/svg\'><path d=\'M7 10l5 5 5-5z\'/></svg>") no-repeat right 2px center', backgroundSize: '16px' }}
                            onChange={(e) => {
                                if (e.target.value) {
                                    addComponent(e.target.value);
                                    e.target.value = "";
                                }
                            }}
                        >
                            <option value="" disabled selected>+ Sensor</option>
                            <option value="dht11">DHT11 (Temp/Hum)</option>
                            <option value="dht22">DHT22 (Temp/Hum)</option>
                            <option value="hc-sr04">HC-SR04 (Distance)</option>
                        </select>
                        <div className="sim-toolbar-divider" />
                        {running ? (
                            <button className="sim-btn sim-btn-stop" onClick={handleStop} style={{ padding: '13px 12px', fontSize: '11px', height: '24px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg> Stop
                            </button>
                        ) : (
                            <button className="sim-btn sim-btn-restart" onClick={handleRestart} style={{ padding: '13px 12px', fontSize: '11px', height: '24px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg> Start
                            </button>
                        )}
                    </div>
                </div>

                <div className="simulator-body">
                    {/* Visual Canvas */}
                    <div
                        className="sim-canvas"
                        onMouseMove={handleCanvasMouseMove}
                        onMouseUp={handleCanvasMouseUp}
                        onMouseLeave={handleCanvasMouseUp}
                    >
                        {/* Zoomable Layer */}
                        <div style={{ transform: `scale(${zoom})`, transformOrigin: '0 0', width: '100%', height: '100%' }}>
                            {/* Wires Overlay */}
                            <svg className="sim-canvas-svg">
                                {wires.map((w, index) => {
                                    const start = getPinCoords(w.from.compId, w.from.pin);
                                    const end = getPinCoords(w.to.compId, w.to.pin);
                                    return (
                                        <path
                                            key={w.id}
                                            d={generateOrthogonalPath(start.x, start.y, end.x, end.y, index)}
                                            className="sim-wire"
                                            stroke={w.color}
                                            fill="none"
                                            onClick={(e) => handleWireClick(e, w.id)}
                                        />
                                    );
                                })}
                                {drawingWire && (
                                    <path
                                        d={generateOrthogonalPath(drawingWire.startX, drawingWire.startY, drawingWire.endX, drawingWire.endY)}
                                        className="sim-wire-preview"
                                        fill="none"
                                    />
                                )}
                            </svg>

                            {/* Components */}
                            {components.map(c => (
                                <div
                                    key={c.id}
                                    className={`sim-canvas-component ${c.type}`}
                                    style={{ left: c.x, top: c.y }}
                                    onMouseDown={(e) => {
                                        setDraggingComp({
                                            id: c.id,
                                            initialCompX: c.x,
                                            initialCompY: c.y,
                                            initialMouseX: e.clientX,
                                            initialMouseY: e.clientY
                                        });
                                    }}
                                >
                                    {c.id !== 'uno' && (
                                        <button className="sim-comp-delete" onClick={(e) => { e.stopPropagation(); removeComponent(c.id); }}>✕</button>
                                    )}

                                    {c.type === 'uno' && <wokwi-arduino-uno />}
                                    {c.type === 'led' && (
                                        <wokwi-led
                                            color={c.color}
                                            ref={(el) => {
                                                if (el) { el.value = false; el.brightness = 0; }
                                                ledDOMRefs.current[c.id] = el;
                                            }}
                                        />
                                    )}
                                    {c.type === 'btn' && (
                                        <wokwi-pushbutton
                                            color={c.color}
                                            ref={(el) => {
                                                if (!el) return;
                                                if (!el._hasListeners) {
                                                    el._hasListeners = true;
                                                    el.addEventListener('button-press', () => handleButtonPressRef.current(c.id));
                                                    el.addEventListener('button-release', () => handleButtonReleaseRef.current(c.id));
                                                }
                                            }}
                                        />
                                    )}

                                    {(c.type === 'dht11' || c.type === 'dht22') && (
                                        <div 
                                            className={`sim-dht-sensor ${c.type}`}
                                            onDoubleClick={(e) => {
                                                e.stopPropagation();
                                                setActiveSensorId(c.id);
                                            }}
                                            title="Double-click to adjust values"
                                        >
                                            <div className="dht-body">
                                                <div className="dht-grill">
                                                    {[...Array(6)].map((_, i) => <div key={i} className="grill-line" />)}
                                                </div>
                                                <button 
                                                    className="dht-settings-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setActiveSensorId(activeSensorId === c.id ? null : c.id);
                                                    }}
                                                >
                                                    ⚙️
                                                </button>
                                                <div className="dht-label">{c.type.toUpperCase()}</div>
                                                <div className="dht-readout">
                                                    <div className="readout-item">{dhtValues[c.id]?.temp ?? 25}°C</div>
                                                    <div className="readout-item">{dhtValues[c.id]?.humidity ?? 60}%</div>
                                                </div>
                                                <div className="dht-pins-labels">
                                                    <span title="VCC">V</span>
                                                    <span title="DATA">D</span>
                                                    <span title="GND">G</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {c.type === 'hc-sr04' && (
                                        <div 
                                            className="sim-hc-sr04-wrapper"
                                            onDoubleClick={(e) => {
                                                e.stopPropagation();
                                                setActiveSensorId(c.id);
                                            }}
                                            title="Double-click to adjust distance"
                                            style={{ position: 'relative', display: 'inline-block' }}
                                        >
                                            <button 
                                                className="dht-settings-btn"
                                                style={{ zIndex: 40, top: '0px', left: '-15px' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveSensorId(activeSensorId === c.id ? null : c.id);
                                                }}
                                            >
                                                ⚙️
                                            </button>
                                            <wokwi-hc-sr04 distance={hcsr04Values[c.id]?.distance || 100} />
                                        </div>
                                    )}

                                    {/* Pin Hotspots */}
                                    {WOKWI_PINS[c.type].map(pin => (
                                        <div
                                            key={pin.name}
                                            className="sim-pin-hotspot"
                                            style={{ left: pin.x, top: pin.y }}
                                            title={`${c.id} - ${pin.name}`}
                                            onMouseDown={(e) => handlePinMouseDown(e, c.id, pin.name)}
                                            onMouseUp={(e) => handlePinMouseUp(e, c.id, pin.name)}
                                        />
                                    ))}
                                </div>
                            ))}

                            {/* DHT Adjustment Popover */}
                            {activeSensorId && dhtValues[activeSensorId] && (
                                <div 
                                    className="dht-popover" 
                                    style={{ 
                                        left: (components.find(c => c.id === activeSensorId)?.x || 0) + 70,
                                        top: (components.find(c => c.id === activeSensorId)?.y || 0) - 20
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="dht-popover-header">
                                        <span>Adjust {dhtValues[activeSensorId].type}</span>
                                        <button className="dht-popover-close" onClick={() => setActiveSensorId(null)}>✕</button>
                                    </div>
                                    <div className="dht-control-group">
                                        <div className="dht-control-label">
                                            <span>Temperature</span>
                                            <span className="dht-control-value">{dhtValues[activeSensorId].temp.toFixed(1)}°C</span>
                                        </div>
                                        <input 
                                            type="range" 
                                            className="dht-slider"
                                            min={dhtValues[activeSensorId].type === 'DHT11' ? 0 : -40} 
                                            max={dhtValues[activeSensorId].type === 'DHT11' ? 50 : 80} 
                                            step="0.1"
                                            value={dhtValues[activeSensorId].temp}
                                            onChange={(e) => setDhtValues(prev => ({
                                                ...prev,
                                                [activeSensorId]: { ...prev[activeSensorId], temp: parseFloat(e.target.value) }
                                            }))}
                                        />
                                    </div>
                                    <div className="dht-control-group">
                                        <div className="dht-control-label">
                                            <span>Humidity</span>
                                            <span className="dht-control-value">{dhtValues[activeSensorId].humidity.toFixed(1)}%</span>
                                        </div>
                                        <input 
                                            type="range" 
                                            className="dht-slider"
                                            min="0" 
                                            max="100" 
                                            step="0.1"
                                            value={dhtValues[activeSensorId].humidity}
                                            onChange={(e) => setDhtValues(prev => ({
                                                ...prev,
                                                [activeSensorId]: { ...prev[activeSensorId], humidity: parseFloat(e.target.value) }
                                            }))}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* HC-SR04 Adjustment Popover */}
                            {activeSensorId && hcsr04Values[activeSensorId] && (
                                <div 
                                    className="dht-popover" 
                                    style={{ 
                                        left: (components.find(c => c.id === activeSensorId)?.x || 0) + 70,
                                        top: (components.find(c => c.id === activeSensorId)?.y || 0) - 20
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="dht-popover-header">
                                        <span>Adjust HC-SR04</span>
                                        <button className="dht-popover-close" onClick={() => setActiveSensorId(null)}>✕</button>
                                    </div>
                                    <div className="dht-control-group">
                                        <div className="dht-control-label">
                                            <span>Distance</span>
                                            <span className="dht-control-value">{hcsr04Values[activeSensorId].distance.toFixed(1)} cm</span>
                                        </div>
                                        <input 
                                            type="range" 
                                            className="dht-slider"
                                            min="2" 
                                            max="400" 
                                            step="1"
                                            value={hcsr04Values[activeSensorId].distance}
                                            onChange={(e) => setHcsr04Values(prev => ({
                                                ...prev,
                                                [activeSensorId]: { distance: parseFloat(e.target.value) }
                                            }))}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Serial Monitor */}
                    <div className="serial-monitor-panel">
                        <div className="serial-monitor-header">
                            <span className="serial-monitor-title">📟 Serial Monitor</span>
                            <button className="serial-clear-btn" onClick={() => { setSerialOutput(""); serialBufferRef.current = ""; }}>Clear</button>
                        </div>
                        <div className="serial-monitor-output" ref={serialPanelRef}>
                            <pre>{serialOutput || <span className="serial-placeholder">Serial output will appear here...</span>}</pre>
                        </div>
                        <div className="serial-input-row">
                            <input
                                className="serial-input"
                                type="text"
                                value={serialInput}
                                onChange={(e) => setSerialInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && serialInput) {
                                        for (const ch of serialInput) serialInputQueue.current.push(ch.charCodeAt(0));
                                        serialInputQueue.current.push(10);
                                        setSerialInput("");
                                    }
                                }}
                                placeholder="Type and press Enter to send..."
                            />
                            <button className="serial-send-btn" onClick={() => {
                                if (serialInput) {
                                    for (const ch of serialInput) serialInputQueue.current.push(ch.charCodeAt(0));
                                    serialInputQueue.current.push(10);
                                    setSerialInput("");
                                }
                            }}>Send</button>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ArduinoSimulator;
